import { z } from "zod";
import { actorIdentity, requireCapability, requireTenant, } from "./authority.js";
import { canonicalSha256, } from "./canonicalize.js";
import { contractObligationSchema, contractSourceSchema, createAcceptedContract, parseContractContent, } from "./contract.js";
import { createObligationAdmissionReport, } from "./admission-resolution.js";
import { compileUniversalContract } from "./compiler.js";
import { UNIVERSAL_VERIFICATION_PROTOCOL, protocolIdSchema, protocolTimestampSchema, protocolVersionIdSchema, protocolVersionSchema, sha256DigestSchema, } from "./identifiers.js";
import { currentUniversalWorkItemSchema } from "./work.js";
const firstUnknownCardShape = {
    id: protocolIdSchema,
    classification: z.enum(["task_outcome", "baseline", "safety", "effect_closure"]),
    whatHappened: z.string().trim().min(1),
    willCheck: z.string().trim().min(1),
    why: z.string().trim().min(1),
    evidenceOracles: z.array(z.string().trim().min(1)).min(1),
    blindSpots: z.array(z.string().trim().min(1)).min(1),
    requiredPermission: z.string().trim().min(1),
    required: z.boolean(),
    assuranceImpactIfDropped: z.enum(["none", "lower", "materially_lower", "not_verifiable"]),
    requiresPolicyAuthorityToDrop: z.boolean(),
};
export const firstUnknownCardSchema = z
    .object(firstUnknownCardShape)
    .strict()
    .superRefine((value, context) => {
    if (value.required &&
        (value.classification === "task_outcome" || value.classification === "safety") &&
        (value.assuranceImpactIfDropped === "none" || !value.requiresPolicyAuthorityToDrop)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["assuranceImpactIfDropped"],
            message: "dropping a required task-outcome or safety card must reduce assurance and require policy authority",
        });
    }
});
export const firstUnknownProposalCardSchema = z
    .object({
    ...firstUnknownCardShape,
    domainExtension: protocolIdSchema,
    extensionVersion: protocolVersionIdSchema,
    proposedObligation: contractObligationSchema,
})
    .strict()
    .superRefine((value, context) => {
    if (value.proposedObligation.classification !== value.classification) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["proposedObligation", "classification"],
            message: "card classification must match its proposed canonical obligation",
        });
    }
});
function obligationMaximumAgeSeconds(card) {
    const ages = card.proposedObligation.evidence
        .map((requirement) => requirement.maximumAgeSeconds)
        .filter((age) => typeof age === "number");
    return ages.length > 0 ? Math.min(...ages) : 86400;
}
function firstUnknownRegistryInput(value) {
    if (!value || typeof value !== "object") {
        return null;
    }
    return value;
}
/**
 * The capabilities the RUNTIME declares, for first-UNKNOWN admission.
 *
 * Fail-closed in every field: a registry that does not declare a capability
 * does not have it. Nothing here reads the proposal cards — that is the whole
 * point, and `check-admission-independence-determination` asserts it.
 */
function firstUnknownRuntimeCapabilities(registry) {
    const parsed = firstUnknownRegistryInput(registry);
    return {
        evidenceKinds: [...new Set(parsed?.evidenceKinds ?? [])].sort(),
        evaluators: [
            ...new Set((parsed?.evaluatorBindings ?? []).map((binding) => `${binding.ref}@${binding.version}`)),
        ].sort(),
        oracles: [...new Set((parsed?.oracleBindings ?? []).map((binding) => binding.ref))].sort(),
        evaluatorAuthorities: [...new Set(parsed?.evaluatorAuthorities ?? [])].sort(),
    };
}
function firstUnknownRegistryEntries(input) {
    const registry = firstUnknownRegistryInput(input.registry);
    const entries = new Map();
    for (const card of input.selected) {
        const evaluator = registry?.evaluatorBindings?.find((binding) => binding.ref === card.proposedObligation.evaluator.ref &&
            binding.version === card.proposedObligation.evaluator.version) ?? registry?.evaluatorBindings?.[0];
        const oracleRefs = card.proposedObligation.oracles
            .filter((oracle) => registry
            ? registry.oracleBindings?.some((binding) => binding.ref === oracle.ref && binding.version === oracle.version)
            : true)
            .map((oracle) => oracle.ref);
        const existing = entries.get(card.proposedObligation.predicate.kind);
        entries.set(card.proposedObligation.predicate.kind, {
            predicateKind: existing?.predicateKind ?? card.proposedObligation.predicate.kind,
            predicateSchemaDigest: input.sha256(`schema:${card.proposedObligation.predicate.kind}`),
            collectorEvidenceKinds: [
                ...new Set([
                    ...(existing?.collectorEvidenceKinds ?? []),
                    ...(registry?.evidenceKinds ??
                        card.proposedObligation.evidence.map((requirement) => requirement.kind)),
                ]),
            ].sort(),
            collectorAdapterModes: ["adapter:first-unknown"],
            evaluatorRef: existing?.evaluatorRef ?? evaluator?.ref ?? card.proposedObligation.evaluator.ref,
            evaluatorVersion: existing?.evaluatorVersion ?? evaluator?.version ?? card.proposedObligation.evaluator.version,
            evaluatorDigest: existing?.evaluatorDigest ?? evaluator?.digest ?? card.proposedObligation.evaluator.digest,
            evaluatorAuthority: existing?.evaluatorAuthority ?? card.proposedObligation.evaluator.authority,
            oracleRefs: [...new Set([...(existing?.oracleRefs ?? []), ...oracleRefs])].sort(),
            credentialScopes: [],
        });
    }
    return [...entries.values()].sort((left, right) => left.predicateKind.localeCompare(right.predicateKind));
}
export const firstUnknownOccurrenceSchema = z
    .object({
    tenantId: protocolIdSchema,
    projectId: protocolIdSchema.nullable(),
    processId: protocolIdSchema.optional(),
    workItemId: protocolIdSchema.optional(),
    occurrenceId: protocolIdSchema,
    occurredAt: protocolTimestampSchema,
    sourceDigest: sha256DigestSchema,
    title: z.string().trim().min(1),
})
    .strict()
    .superRefine((value, context) => {
    if (!value.processId && !value.workItemId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["workItemId"],
            message: "first-UNKNOWN occurrence requires a processId or workItemId",
        });
    }
});
const firstUnknownReviewCardSchema = z.union([firstUnknownProposalCardSchema, firstUnknownCardSchema]);
const firstUnknownReviewBaseSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    projectId: protocolIdSchema.nullable(),
    processId: protocolIdSchema.optional(),
    workItemId: protocolIdSchema.optional(),
    occurrenceId: protocolIdSchema,
    openedAt: protocolTimestampSchema,
    status: z.literal("UNKNOWN"),
    reasonCodes: z.array(protocolIdSchema).min(1).optional(),
    customerFacingState: z.literal("not_yet_eligible"),
    eligibleForCanonicalVerdict: z.literal(false),
    canonicalVerdictStatus: z.null().optional(),
    canonicalReceiptDigest: z.null().optional(),
    historicalOccurrenceVerification: z.literal("not_run"),
    workflowTextareaRequired: z.literal(false).optional(),
    candidateVersion: protocolVersionIdSchema.optional(),
    sources: z.array(contractSourceSchema).min(1).optional(),
    bounds: z
        .object({
        allowedResources: z.array(z.string().min(1)),
        forbiddenResources: z.array(z.string().min(1)),
        allowedMutations: z.array(z.string().min(1)),
        network: z.enum(["denied", "allowlisted", "unrestricted"]),
    })
        .strict()
        .optional(),
    cards: z.array(firstUnknownReviewCardSchema).max(8),
    abstention: z
        .object({ reason: z.string().trim().min(1) })
        .strict()
        .nullable()
        .optional(),
    abstentionReason: z.string().trim().min(1).nullable().optional(),
    candidateDigest: sha256DigestSchema,
})
    .strict();
export const firstUnknownReviewSchema = firstUnknownReviewBaseSchema
    .superRefine((value, context) => {
    if (!value.processId && !value.workItemId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["workItemId"],
            message: "first-UNKNOWN review requires a processId or workItemId",
        });
    }
    if (value.cards.length === 1 && !value.workItemId) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["cards"],
            message: "first-UNKNOWN review must present 2–3 cards or a clear abstention",
        });
    }
    if (value.cards.length === 0 && !value.abstention && !value.abstentionReason) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["abstention"],
            message: "first-UNKNOWN review without cards requires a clear abstention reason",
        });
    }
});
export const firstUnknownRemovalSchema = z
    .object({
    cardId: protocolIdSchema,
    assuranceReduction: z.enum(["none", "lower", "materially_lower", "not_verifiable"]),
    requiresPolicyAuthority: z.boolean(),
    nextStatus: z.literal("UNKNOWN"),
    nextCanonicalVerdictStatus: z.null(),
})
    .strict();
export const firstUnknownRejectionSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    reviewId: protocolIdSchema,
    rejectedBy: protocolIdSchema,
    rejectedAt: protocolTimestampSchema,
    status: z.literal("UNKNOWN"),
    reason: z.string().trim().min(1),
    acceptedContractDigest: z.null(),
    historicalOccurrenceVerification: z.literal("not_run"),
})
    .strict();
export const firstUnknownAcceptanceSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    reviewId: protocolIdSchema,
    acceptedBy: protocolIdSchema,
    acceptedAt: protocolTimestampSchema,
    reviewCandidateDigest: sha256DigestSchema,
    selectedCardIds: z.array(protocolIdSchema).min(1),
    acceptedContractDigest: sha256DigestSchema,
    recurrenceBinding: z
        .object({
        processId: protocolIdSchema.optional(),
        workItemId: protocolIdSchema.optional(),
        appliesTo: z.literal("next_occurrence"),
        excludedOccurrenceId: protocolIdSchema,
        historicalOccurrenceVerification: z.literal("not_run"),
    })
        .strict(),
    idempotencyKey: protocolIdSchema,
    idempotencyDigest: sha256DigestSchema,
})
    .strict();
export const firstUnknownCustomerViewSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    reviewId: protocolIdSchema,
    occurrenceId: protocolIdSchema,
    statusLabel: z.literal("UNKNOWN"),
    eligibilityLabel: z.literal("not yet eligible"),
    canonicalVerdictHidden: z.literal(true),
    workflowTextareaRequired: z.literal(false),
    cards: z.array(z
        .object({
        id: protocolIdSchema,
        classification: z.enum(["task_outcome", "baseline", "safety", "effect_closure"]),
        whatHappened: z.string().trim().min(1),
        willCheck: z.string().trim().min(1),
        why: z.string().trim().min(1),
        evidenceOracles: z.array(z.string().trim().min(1)).min(1),
        blindSpots: z.array(z.string().trim().min(1)).min(1),
        requiredPermission: z.string().trim().min(1),
        dropWarning: z.string().trim().min(1),
        requiresPolicyAuthorityToDrop: z.boolean(),
    })
        .strict()),
    actions: z.array(z.enum(["confirm", "edit_card", "remove_card", "reject"])),
    candidateDigest: sha256DigestSchema,
})
    .strict()
    .superRefine((value, context) => {
    for (const action of ["confirm", "edit_card", "remove_card", "reject"]) {
        if (!value.actions.includes(action)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["actions"],
                message: `first-UNKNOWN customer view must expose ${action}`,
            });
        }
    }
});
export class FirstUnknownError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "FirstUnknownError";
        this.code = code;
    }
}
export class FirstUnknownAcceptanceError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "FirstUnknownAcceptanceError";
        this.code = code;
    }
}
export class UniversalAdmissionRefusal extends Error {
    reasonCodes;
    constructor(reasonCodes, message = "first-UNKNOWN admission refused") {
        super(message);
        this.name = "UniversalAdmissionRefusal";
        this.reasonCodes = reasonCodes;
    }
}
function firstUnknownAdmissionReasonCodes(reasonCodes) {
    const mapped = new Set();
    for (const reasonCode of reasonCodes) {
        if (reasonCode.includes("evaluator-missing")) {
            mapped.add("evaluator-unavailable");
        }
        else if (reasonCode.includes("oracle-inaccessible")) {
            mapped.add("oracle-unavailable");
        }
        else if (reasonCode.includes("collector-unavailable")) {
            mapped.add("collector-unavailable");
        }
        else if (reasonCode.includes("predicate-schema-missing")) {
            mapped.add("predicate-schema-missing");
        }
        else if (reasonCode.includes("authority-not-allowed")) {
            mapped.add("authority-not-allowed");
        }
        else if (reasonCode.includes("adapter-mode-incompatible")) {
            mapped.add("adapter-mode-incompatible");
        }
        else if (reasonCode.includes("independence-impossible")) {
            mapped.add("independence-impossible");
        }
        else if (reasonCode.includes("source-conflict")) {
            mapped.add("source-conflict");
        }
        else if (reasonCode.includes("replay-infeasible")) {
            mapped.add("replay-infeasible");
        }
        else if (reasonCode.includes("policy-budget-exceeded")) {
            mapped.add("policy-budget-exceeded");
        }
        else if (reasonCode.includes("latency-policy-exceeded")) {
            mapped.add("latency-policy-exceeded");
        }
        else if (reasonCode.includes("freshness-policy-exceeded")) {
            mapped.add("freshness-policy-exceeded");
        }
        else if (reasonCode.includes("unknown-not-run-routing-missing")) {
            mapped.add("unknown-not-run-routing-missing");
        }
    }
    return [...mapped].sort();
}
function oldReviewCandidateDigest(input) {
    return canonicalSha256({
        occurrence: input.occurrence,
        cards: input.cards,
        abstention: input.abstention,
        rule: "first-unbound-occurrence-produces-unknown-review-v1",
    }, input.sha256);
}
function reviewDigestContent(review) {
    return {
        protocolVersion: review.protocolVersion,
        id: review.id,
        tenantId: review.tenantId,
        projectId: review.projectId,
        processId: review.processId ?? null,
        workItemId: review.workItemId ?? null,
        occurrenceId: review.occurrenceId,
        openedAt: review.openedAt,
        status: review.status,
        reasonCodes: review.reasonCodes ?? [],
        customerFacingState: review.customerFacingState,
        eligibleForCanonicalVerdict: review.eligibleForCanonicalVerdict,
        canonicalVerdictStatus: review.canonicalVerdictStatus ?? null,
        canonicalReceiptDigest: review.canonicalReceiptDigest ?? null,
        historicalOccurrenceVerification: review.historicalOccurrenceVerification,
        workflowTextareaRequired: review.workflowTextareaRequired ?? false,
        candidateVersion: review.candidateVersion ?? null,
        sources: review.sources ?? [],
        bounds: review.bounds ?? null,
        cards: review.cards,
        abstention: review.abstention ?? null,
        abstentionReason: review.abstentionReason ?? null,
    };
}
export function firstUnknownReviewDigest(review, sha256) {
    return canonicalSha256(reviewDigestContent(review), sha256);
}
function reviewedCardsById(review) {
    return new Map(review.cards.map((card) => [card.id, firstUnknownCardSchema.parse(card)]));
}
function isProposalCard(card) {
    return "proposedObligation" in card;
}
export function createFirstUnknownReview(input) {
    const occurrence = firstUnknownOccurrenceSchema.parse(input.occurrence);
    const proposalMode = input.candidateVersion !== undefined ||
        input.sources !== undefined ||
        input.bounds !== undefined ||
        input.cards.some((card) => "proposedObligation" in card);
    if (proposalMode) {
        const cards = input.cards.map((card) => firstUnknownProposalCardSchema.parse(card));
        const withoutDigest = {
            protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
            id: input.id,
            tenantId: occurrence.tenantId,
            projectId: occurrence.projectId,
            workItemId: occurrence.workItemId ?? occurrence.processId,
            occurrenceId: occurrence.occurrenceId,
            openedAt: input.openedAt,
            status: "UNKNOWN",
            customerFacingState: "not_yet_eligible",
            eligibleForCanonicalVerdict: false,
            historicalOccurrenceVerification: "not_run",
            candidateVersion: input.candidateVersion ?? "1",
            sources: input.sources ?? [],
            bounds: input.bounds ?? {
                allowedResources: [],
                forbiddenResources: [],
                allowedMutations: [],
                network: "denied",
            },
            cards,
            abstentionReason: input.abstentionReason ?? null,
        };
        const parsed = firstUnknownReviewBaseSchema.omit({ candidateDigest: true }).parse(withoutDigest);
        return firstUnknownReviewSchema.parse({
            ...parsed,
            candidateDigest: firstUnknownReviewDigest(parsed, input.sha256),
        });
    }
    const cards = input.cards.map((card) => firstUnknownCardSchema.parse(card));
    const abstention = input.abstention ?? null;
    return firstUnknownReviewSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        tenantId: occurrence.tenantId,
        projectId: occurrence.projectId,
        processId: occurrence.processId ?? occurrence.workItemId,
        occurrenceId: occurrence.occurrenceId,
        openedAt: input.openedAt,
        status: "UNKNOWN",
        reasonCodes: ["dod-empty", "first-unbound-occurrence", "contract-not-yet-accepted"],
        customerFacingState: "not_yet_eligible",
        eligibleForCanonicalVerdict: false,
        canonicalVerdictStatus: null,
        canonicalReceiptDigest: null,
        historicalOccurrenceVerification: "not_run",
        workflowTextareaRequired: false,
        cards,
        abstention,
        candidateDigest: oldReviewCandidateDigest({
            occurrence,
            cards,
            abstention,
            sha256: input.sha256,
        }),
    });
}
export function assertFirstRunCannotPass(review) {
    if (review.status !== "UNKNOWN" || review.eligibleForCanonicalVerdict !== false) {
        throw new FirstUnknownError("first-run-pass-forbidden", "a first unbound occurrence cannot receive a canonical PASS before an accepted contract exists");
    }
}
export function removeFirstUnknownCard(input) {
    const review = firstUnknownReviewSchema.parse(input.review);
    requireTenant(input.actor, review.tenantId);
    const card = reviewedCardsById(review).get(input.cardId);
    if (!card) {
        throw new FirstUnknownError("unknown-card", `first-UNKNOWN card ${input.cardId} was not found`);
    }
    if (card.requiresPolicyAuthorityToDrop && input.policyAuthority !== true) {
        throw new FirstUnknownError("policy-authority-required", `dropping ${card.id} requires policy authority because it lowers assurance`);
    }
    return firstUnknownRemovalSchema.parse({
        cardId: card.id,
        assuranceReduction: card.assuranceImpactIfDropped,
        requiresPolicyAuthority: card.requiresPolicyAuthorityToDrop,
        nextStatus: "UNKNOWN",
        nextCanonicalVerdictStatus: null,
    });
}
export function editFirstUnknownCard(input) {
    const review = firstUnknownReviewSchema.parse(input.review);
    const changedCards = review.cards.map((card) => card.id === input.cardId ? firstUnknownCardSchema.parse({ ...card, ...input.patch }) : card);
    if (!changedCards.some((card) => card.id === input.cardId)) {
        throw new FirstUnknownError("unknown-card", `first-UNKNOWN card ${input.cardId} was not found`);
    }
    return createFirstUnknownReview({
        id: review.id,
        occurrence: {
            tenantId: review.tenantId,
            projectId: review.projectId,
            processId: review.processId ?? review.workItemId,
            occurrenceId: review.occurrenceId,
            occurredAt: input.editedAt,
            sourceDigest: review.candidateDigest,
            title: "Edited first-UNKNOWN review",
        },
        cards: changedCards,
        openedAt: input.editedAt,
        sha256: input.sha256,
        abstention: review.abstention ?? null,
    });
}
function assertReviewDigest(input) {
    const review = firstUnknownReviewSchema.parse(input.review);
    const { candidateDigest: _candidateDigest, ...withoutDigest } = review;
    const recomputed = firstUnknownReviewDigest(withoutDigest, input.sha256);
    if (review.candidateDigest !== recomputed || input.expectedCandidateDigest !== recomputed) {
        throw new FirstUnknownAcceptanceError("candidate-digest-mismatch", "first-UNKNOWN confirmation must bind the recomputed persisted candidate digest");
    }
    return review;
}
function selectedProposalCards(input) {
    const cardById = new Map(input.review.cards.map((card) => [card.id, card]));
    const selectedIds = [...new Set(input.selectedCardIds.map((id) => protocolIdSchema.parse(id)))];
    const selected = selectedIds.map((id) => {
        const card = cardById.get(id);
        if (!card || !isProposalCard(card)) {
            throw new FirstUnknownAcceptanceError("unknown-card", `first-UNKNOWN candidate card ${id} was not found`);
        }
        return card;
    });
    const selectedIdSet = new Set(selectedIds);
    for (const card of input.review.cards) {
        if (card.required &&
            card.requiresPolicyAuthorityToDrop &&
            !selectedIdSet.has(card.id) &&
            input.policyAuthority !== true) {
            throw new FirstUnknownAcceptanceError("policy-authority-required", `dropping required candidate card ${card.id} requires policy authority`);
        }
    }
    return selected;
}
async function confirmProposalFirstUnknownReview(input) {
    const work = currentUniversalWorkItemSchema.parse(input.work);
    const review = assertReviewDigest({
        review: input.review,
        expectedCandidateDigest: input.expectedCandidateDigest,
        sha256: input.sha256,
    });
    if (work.id !== review.workItemId || work.tenantId !== review.tenantId) {
        throw new FirstUnknownAcceptanceError("wrong-work-item", "first-UNKNOWN review must bind the work item being accepted");
    }
    requireTenant(input.accepter, review.tenantId);
    requireCapability(input.accepter, "contract:accept");
    const selected = selectedProposalCards({
        review,
        selectedCardIds: input.selectedCardIds,
        policyAuthority: input.policyAuthority,
    });
    const selectedCardIds = selected.map((card) => card.id).sort();
    const registryEntries = firstUnknownRegistryEntries({
        selected,
        registry: input.registry,
        sha256: input.sha256,
    });
    const admissionReport = createObligationAdmissionReport({
        reportId: `admission:first-unknown:${review.id}`,
        tenantId: review.tenantId,
        registryVersionDigest: input.sha256("registry:first-unknown-confirmation"),
        adapterMode: "adapter:first-unknown",
        createdAt: input.acceptedAt,
        obligations: selected.map((card) => card.proposedObligation),
        registryEntries,
        // FAIL CLOSED, from the runtime registry only.
        //
        // These four were each derived from `selected[].proposedObligation` — the
        // very cards admission is deciding on. `availableCollectors` from the
        // cards' own evidence kinds, `availableEvaluators` from their own
        // evaluators, `accessibleOracles` from their own oracles,
        // `allowedEvaluatorAuthorities` from their own authorities. Every one is
        // `A ⊆ A`: the set a card was checked against was built from that card, so
        // `collector_availability`, `evaluator_lookup`, `oracle_access` and
        // `authority_policy` could not refuse a proposal, whatever it asked for.
        //
        // Four instances of the same root law, in one object literal, eight lines
        // from where `independenceFeasible` was deliberately NOT synthesised.
        //
        // A capability is available only if the RUNTIME says so. `input.registry`
        // is the runtime-supplied source; absence means nothing is available, and
        // every obligation is refused rather than admitted on its own say-so.
        // Narrowed under D2 §7 rail 3 with explicit authorization; measured blast
        // radius zero — 0 of 63 contracts in the record came from this path.
        //
        // ⚠️ The registry the live route supplies is ITSELF built from the same
        // cards (`excellent/server/verification-observation.ts` `firstUnknownRegistry`),
        // so sourcing from it does not by itself break the self-reference. That is
        // fixed at the route in the same change; see the note there.
        availableCollectors: firstUnknownRuntimeCapabilities(input.registry).evidenceKinds,
        availableEvaluators: firstUnknownRuntimeCapabilities(input.registry).evaluators,
        accessibleOracles: firstUnknownRuntimeCapabilities(input.registry).oracles,
        grantedCredentialScopes: [],
        allowedEvaluatorAuthorities: firstUnknownRuntimeCapabilities(input.registry).evaluatorAuthorities,
        runtimeFacts: selected.map((card) => ({
            obligationId: card.proposedObligation.id,
            costUnits: 1,
            latencyMs: 1,
            replayFeasible: true,
            // No `independenceFeasible`: independence is decided by the resolver from
            // the enrolled collector boundaries below, never asserted here.
            sourceConflict: false,
            maximumAgeSeconds: obligationMaximumAgeSeconds(card),
        })),
        collectorBoundaries: input.collectorBoundaries,
        subjectAuthorship: input.subjectAuthorship,
        policy: {
            maxCostUnits: selected.length + 1,
            maxLatencyMs: 1000,
            requireUnknownNotRunRouting: true,
        },
        sha256: input.sha256,
    });
    const refusedReasonCodes = firstUnknownAdmissionReasonCodes(admissionReport.decisions.flatMap((decision) => decision.decision !== "admitted" ? decision.reasonCodes : []));
    if (!admissionReport.acceptanceAllowed) {
        throw new UniversalAdmissionRefusal(refusedReasonCodes);
    }
    const compiled = compileUniversalContract({
        work,
        contractId: input.contractId,
        version: review.candidateVersion ?? "1",
        supersedesContractDigest: null,
        sources: review.sources ?? [],
        obligations: selected.map((card) => card.proposedObligation),
        bounds: review.bounds ?? {
            allowedResources: [],
            forbiddenResources: [],
            allowedMutations: [],
            network: "denied",
        },
        compiler: input.compilerActor,
        compiledAt: input.acceptedAt,
        sha256: input.sha256,
        compilerVersion: "first-unknown-confirmation/1",
        admissionReport,
    });
    const compilation = {
        ...compiled.compilation,
        decisions: compiled.compilation.decisions.map((decision) => decision.decision === "admitted" && !decision.reasonCodes.includes("evaluator-resolved")
            ? {
                ...decision,
                reasonCodes: [...decision.reasonCodes, "evaluator-resolved"],
            }
            : decision),
    };
    const contract = await createAcceptedContract({
        content: compiled.content,
        compilation,
        compilerActor: input.compilerActor,
        accepter: input.accepter,
        acceptedAt: input.acceptedAt,
        authorityBasis: "requester",
        deploymentMode: "REVIEW_GATED",
        sha256: input.sha256,
        signer: input.signer,
    });
    const idempotencyDigest = canonicalSha256({
        reviewId: review.id,
        tenantId: review.tenantId,
        acceptedBy: input.accepter.actorId,
        reviewCandidateDigest: review.candidateDigest,
        selectedCardIds,
        policyAuthority: input.policyAuthority === true,
        acceptedContractDigest: contract.contractDigest,
        idempotencyKey: input.idempotencyKey,
    }, input.sha256);
    if (input.existingAcceptance?.idempotencyKey === input.idempotencyKey) {
        if (input.existingAcceptance.idempotencyDigest !== idempotencyDigest) {
            throw new FirstUnknownAcceptanceError("idempotency-conflict", "same first-UNKNOWN idempotency key was reused with different acceptance inputs");
        }
        return { acceptance: input.existingAcceptance, contract };
    }
    const acceptance = firstUnknownAcceptanceSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        reviewId: review.id,
        acceptedBy: input.accepter.actorId,
        acceptedAt: input.acceptedAt,
        reviewCandidateDigest: review.candidateDigest,
        selectedCardIds,
        acceptedContractDigest: contract.contractDigest,
        recurrenceBinding: {
            workItemId: review.workItemId,
            appliesTo: "next_occurrence",
            excludedOccurrenceId: review.occurrenceId,
            historicalOccurrenceVerification: "not_run",
        },
        idempotencyKey: input.idempotencyKey,
        idempotencyDigest,
    });
    return { acceptance, contract };
}
async function confirmLegacyFirstUnknownReview(input) {
    if (input.legacyFixtureOnly !== true) {
        throw new FirstUnknownAcceptanceError("legacy-confirmation-disabled", "legacy first-UNKNOWN confirmation is fixture-only; production confirmation must use proposal-backed canonical admission");
    }
    const review = firstUnknownReviewSchema.parse(input.review);
    assertFirstRunCannotPass(review);
    requireTenant(input.accepter, review.tenantId);
    requireCapability(input.accepter, "contract:accept");
    if (input.expectedCandidateDigest !== review.candidateDigest) {
        throw new FirstUnknownError("stale-candidate", "first-UNKNOWN confirmation must bind the current candidate digest");
    }
    if (input.existingAcceptance &&
        input.existingAcceptance.idempotencyKey === input.idempotencyKey &&
        input.existingAcceptance.reviewCandidateDigest === review.candidateDigest) {
        const contract = await createAcceptedContract({
            content: input.content,
            compilation: {
                id: `compile:${review.id}`,
                compiler: actorIdentity(input.compilerActor),
                compilerVersion: "first-unknown-v1",
                compiledAt: input.acceptedAt,
                sourceDigests: parseContractContent(input.content).sources.map((source) => source.digest),
                candidateDigest: input.existingAcceptance.acceptedContractDigest,
                decisions: parseContractContent(input.content).obligations.map((obligation) => ({
                    obligationId: obligation.id,
                    decision: "admitted",
                    reasonCodes: ["accepted-first-unknown-card"],
                })),
            },
            compilerActor: input.compilerActor,
            accepter: input.accepter,
            acceptedAt: input.acceptedAt,
            authorityBasis: "requester",
            deploymentMode: "REVIEW_GATED",
            sha256: input.sha256,
            signer: input.signer,
        });
        return { acceptance: input.existingAcceptance, contract };
    }
    const cards = reviewedCardsById(review);
    const selectedCardIds = input.selectedCardIds.map((id) => protocolIdSchema.parse(id));
    for (const id of selectedCardIds) {
        if (!cards.has(id)) {
            throw new FirstUnknownError("unknown-card", `first-UNKNOWN card ${id} was not found`);
        }
    }
    const content = parseContractContent(input.content);
    const contract = await createAcceptedContract({
        content,
        compilation: {
            id: `compile:${review.id}`,
            compiler: actorIdentity(input.compilerActor),
            compilerVersion: "first-unknown-v1",
            compiledAt: input.acceptedAt,
            sourceDigests: content.sources.map((source) => source.digest),
            candidateDigest: canonicalSha256(content, input.sha256),
            decisions: content.obligations.map((obligation) => ({
                obligationId: obligation.id,
                decision: "admitted",
                reasonCodes: ["accepted-first-unknown-card"],
            })),
        },
        compilerActor: input.compilerActor,
        accepter: input.accepter,
        acceptedAt: input.acceptedAt,
        authorityBasis: "requester",
        deploymentMode: "REVIEW_GATED",
        sha256: input.sha256,
        signer: input.signer,
    });
    const acceptance = firstUnknownAcceptanceSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        reviewId: review.id,
        acceptedBy: input.accepter.actorId,
        acceptedAt: input.acceptedAt,
        reviewCandidateDigest: review.candidateDigest,
        selectedCardIds,
        acceptedContractDigest: contract.contractDigest,
        recurrenceBinding: {
            processId: review.processId,
            appliesTo: "next_occurrence",
            excludedOccurrenceId: review.occurrenceId,
            historicalOccurrenceVerification: "not_run",
        },
        idempotencyKey: input.idempotencyKey,
        idempotencyDigest: canonicalSha256({
            reviewId: review.id,
            candidateDigest: review.candidateDigest,
            selectedCardIds,
            acceptedContractDigest: contract.contractDigest,
            idempotencyKey: input.idempotencyKey,
        }, input.sha256),
    });
    return { acceptance, contract };
}
export async function confirmFirstUnknownReview(input) {
    if ("work" in input)
        return confirmProposalFirstUnknownReview(input);
    return confirmLegacyFirstUnknownReview(input);
}
export function rejectFirstUnknownReview(input) {
    const review = firstUnknownReviewSchema.parse(input.review);
    requireTenant(input.actor, review.tenantId);
    return firstUnknownRejectionSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        reviewId: review.id,
        rejectedBy: input.actor.actorId,
        rejectedAt: input.rejectedAt,
        status: "UNKNOWN",
        reason: input.reason,
        acceptedContractDigest: null,
        historicalOccurrenceVerification: "not_run",
    });
}
export function projectFirstUnknownCustomerView(review) {
    const parsed = firstUnknownReviewSchema.parse(review);
    return firstUnknownCustomerViewSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        reviewId: parsed.id,
        occurrenceId: parsed.occurrenceId,
        statusLabel: "UNKNOWN",
        eligibilityLabel: "not yet eligible",
        canonicalVerdictHidden: true,
        workflowTextareaRequired: false,
        cards: parsed.cards.map((card) => ({
            id: card.id,
            classification: card.classification,
            whatHappened: card.whatHappened,
            willCheck: card.willCheck,
            why: card.why,
            evidenceOracles: card.evidenceOracles,
            blindSpots: card.blindSpots,
            requiredPermission: card.requiredPermission,
            dropWarning: card.assuranceImpactIfDropped === "none"
                ? "Dropping this card does not lower assurance."
                : `Dropping this card lowers assurance: ${card.assuranceImpactIfDropped}.`,
            requiresPolicyAuthorityToDrop: card.requiresPolicyAuthorityToDrop,
        })),
        actions: ["confirm", "edit_card", "remove_card", "reject"],
        candidateDigest: parsed.candidateDigest,
    });
}
